import { useEffect, useMemo, useState } from 'react';
import {
  LANDING_CONTENT_EL,
  LANDING_NAMESPACE,
  readSection,
  type LandingContent,
  type LandingSection,
} from '@ah/contracts';
import type { Locale } from '@ah/i18n';
import { useContentApi } from '../content/useContentApi';

/**
 * Landing copy, section by section.
 *
 * Returns the shipped defaults immediately and swaps in published CMS blocks
 * when they arrive, so the page renders complete on first paint — no layout
 * shift while content loads (the brief's CLS < 0.1 target) and no blank page if
 * the API is unreachable. A failed fetch is silent by design: a marketing page
 * should never show an error wall to a first-time visitor.
 */
export function useLandingContent(locale: Locale): LandingContent {
  const contentApi = useContentApi();
  const [raw, setRaw] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    contentApi
      .getContent(LANDING_NAMESPACE, locale)
      .then((res) => {
        if (!cancelled) setRaw(res.content);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [contentApi, locale]);

  return useMemo(() => {
    const out = {} as LandingContent;
    for (const section of Object.keys(LANDING_CONTENT_EL) as LandingSection[]) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (out as any)[section] = readSection(section, raw[section], locale);
    }
    return out;
  }, [raw, locale]);
}
